/* ==========================================================================
   CHLOIA — theme scripts
   Cart drawer · quick add · wishlist · search · phone menu · count-up · tiers
   ========================================================================== */
(() => {
  if (window.Chloia) return;

  const C = window.ChloiaConfig || {};
  const R = window.routes || {};
  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));

  /* ---------- Helpers ---------- */

  const money = (cents) => {
    const value = Number(cents || 0) / 100;
    const hasDecimals = Math.round(value * 100) % 100 !== 0;
    const digits = hasDecimals || !C.hideZeroDecimals ? 2 : 0;
    const formatted = value.toLocaleString('en-IN', { minimumFractionDigits: digits, maximumFractionDigits: 2 });
    return (C.currencySymbol || '') + formatted;
  };

  const escapeHtml = (str) =>
    String(str == null ? '' : str).replace(/[&<>"']/g, (ch) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[ch]);

  const debounce = (fn, wait) => {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn(...args), wait);
    };
  };

  /* ---------- Toast ---------- */

  let toastTimer;
  const toast = (message) => {
    const el = $('[data-ch-toast]');
    if (!el) return;
    $('[data-ch-toast-text]', el).textContent = message;
    el.hidden = false;
    el.style.animation = 'none';
    void el.offsetWidth;
    el.style.animation = '';
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => {
      el.hidden = true;
    }, 2000);
  };

  /* ---------- Layers (drawers / overlays) ---------- */

  const openLayers = [];

  const openLayer = (el, trigger) => {
    if (!el || !el.hidden) return;
    openLayers.forEach((layer) => closeLayer(layer, false));
    el.hidden = false;
    el._trigger = trigger || document.activeElement;
    openLayers.push(el);
    document.body.classList.add('ch-lock');
    const focusTarget = $('[data-ch-search-input]', el) || $('.ch-drawer__panel', el);
    if (focusTarget) setTimeout(() => focusTarget.focus({ preventScroll: true }), 30);
  };

  const closeLayer = (el, restoreFocus = true) => {
    if (!el || el.hidden) return;
    el.hidden = true;
    const i = openLayers.indexOf(el);
    if (i > -1) openLayers.splice(i, 1);
    if (!openLayers.length) document.body.classList.remove('ch-lock');
    if (restoreFocus && el._trigger && document.contains(el._trigger)) {
      try {
        el._trigger.focus({ preventScroll: true });
      } catch (e) {}
    }
  };

  /* ---------- Cart ---------- */

  const Cart = {
    get drawer() {
      return $('[data-ch-cart-drawer]');
    },

    open(trigger) {
      openLayer(this.drawer, trigger);
    },

    close() {
      closeLayer(this.drawer);
    },

    setCount(count) {
      $$('[data-ch-cart-count]').forEach((el) => {
        el.textContent = count;
        el.classList.toggle('is-zero', !count);
      });
    },

    // Cart page sections (sections/ch-main-cart) re-render alongside the drawer
    get pageSections() {
      return $$('[data-ch-cart-section]');
    },

    render(sections) {
      if (!sections) return;
      const parse = (html) => new DOMParser().parseFromString(html, 'text/html');
      const drawerHtml = sections['ch-cart-drawer'];
      if (drawerHtml) {
        const fresh = $('[data-ch-cart-inner]', parse(drawerHtml));
        const current = $('[data-ch-cart-inner]');
        if (fresh && current) current.innerHTML = fresh.innerHTML;
      }
      this.pageSections.forEach((section) => {
        const html = sections[section.getAttribute('data-ch-cart-section')];
        if (!html) return;
        const fresh = $('[data-ch-cart-section-inner]', parse(html));
        const current = $('[data-ch-cart-section-inner]', section);
        if (!fresh || !current) return;
        // Keep an order note that hasn't been saved yet
        const note = $('[data-ch-cart-note]', current);
        const freshNote = $('[data-ch-cart-note]', fresh);
        if (note && freshNote) freshNote.textContent = note.value;
        current.innerHTML = fresh.innerHTML;
      });
      document.dispatchEvent(new CustomEvent('ch:cart:rendered'));
    },

    async request(url, body) {
      const drawer = this.drawer;
      const busy = [drawer, ...this.pageSections].filter(Boolean);
      busy.forEach((el) => el.classList.add('is-busy'));
      const sectionIds = ['ch-cart-drawer', ...this.pageSections.map((el) => el.getAttribute('data-ch-cart-section'))];
      try {
        const response = await fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ ...body, sections: sectionIds.join(','), sections_url: window.location.pathname }),
        });
        const data = await response.json();
        if (!response.ok || data.status) {
          throw new Error(data.description || data.message || (C.strings && C.strings.cartError) || 'Something went wrong');
        }
        this.render(data.sections);
        return data;
      } finally {
        busy.forEach((el) => el.classList.remove('is-busy'));
      }
    },

    async refreshCount() {
      try {
        const cart = await fetch(`${R.cart_url}.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
        this.setCount(cart.item_count);
        this.variantIds = new Set(cart.items.map((i) => String(i.variant_id)));
        document.dispatchEvent(new CustomEvent('ch:cart:updated', { detail: cart }));
        return cart;
      } catch (e) {
        return null;
      }
    },

    async add(items, { silent = false, title = '' } = {}) {
      const data = await this.request(`${R.cart_add_url}.js`, { items });
      await this.refreshCount();
      if (title) toast(`${title} ${(C.strings && C.strings.added) || 'added'}`);
      if (!silent) this.open();
      return data;
    },

    // Add from a product form with a file (design upload). Sent as multipart so Shopify stores the file.
    async addForm(formData, { silent = false, title = '' } = {}) {
      const busy = [this.drawer, ...this.pageSections].filter(Boolean);
      const sectionIds = ['ch-cart-drawer', ...this.pageSections.map((el) => el.getAttribute('data-ch-cart-section'))];
      formData.set('sections', sectionIds.join(','));
      formData.set('sections_url', window.location.pathname);
      busy.forEach((el) => el.classList.add('is-busy'));
      let data;
      try {
        const response = await fetch(R.cart_add_url, {
          method: 'POST',
          headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/javascript' },
          body: formData,
        });
        try {
          data = await response.json();
        } catch (e) {
          throw new Error((C.strings && C.strings.uploadError) || 'Upload failed — try a smaller image');
        }
        if (!response.ok || data.status) {
          throw new Error(data.description || data.message || (C.strings && C.strings.cartError) || 'Something went wrong');
        }
        this.render(data.sections);
      } finally {
        busy.forEach((el) => el.classList.remove('is-busy'));
      }
      await this.refreshCount();
      if (title) toast(`${title} ${(C.strings && C.strings.added) || 'added'}`);
      if (!silent) this.open();
      return data;
    },

    async change(key, quantity) {
      const data = await this.request(`${R.cart_change_url}.js`, { id: key, quantity });
      this.setCount(data.item_count);
      this.variantIds = new Set((data.items || []).map((i) => String(i.variant_id)));
      document.dispatchEvent(new CustomEvent('ch:cart:updated', { detail: data }));
      return data;
    },
  };

  /* ---------- Wishlist ---------- */

  const Wish = {
    key: 'chloia:wishlist',

    get drawer() {
      return $('[data-ch-wish-drawer]');
    },

    read() {
      try {
        const list = JSON.parse(window.localStorage.getItem(this.key) || '[]');
        return Array.isArray(list) ? list : [];
      } catch (e) {
        return [];
      }
    },

    write(list) {
      try {
        window.localStorage.setItem(this.key, JSON.stringify(list));
      } catch (e) {}
      this.sync();
    },

    has(handle) {
      return this.read().some((item) => item.handle === handle);
    },

    toggle(product) {
      const list = this.read();
      const index = list.findIndex((item) => item.handle === product.handle);
      if (index > -1) list.splice(index, 1);
      else list.unshift(product);
      this.write(list);
      return index === -1;
    },

    remove(handle) {
      this.write(this.read().filter((item) => item.handle !== handle));
    },

    open(trigger) {
      Cart.refreshCount().then(() => this.renderList());
      openLayer(this.drawer, trigger);
    },

    close() {
      closeLayer(this.drawer);
    },

    sync() {
      const list = this.read();
      const count = list.length;
      $$('[data-ch-wish-count]').forEach((el) => {
        el.textContent = count;
      });
      $$('[data-ch-wish-char]').forEach((el) => {
        el.textContent = count ? '♥' : '♡';
      });
      $$('[data-ch-wish-pip], [data-ch-wish-icon]').forEach((el) => el.classList.toggle('has-items', count > 0));
      const handles = new Set(list.map((item) => item.handle));
      $$('[data-ch-wish]').forEach((btn) => {
        const active = handles.has(btn.getAttribute('data-ch-wish'));
        btn.classList.toggle('is-active', active);
        btn.setAttribute('aria-pressed', active ? 'true' : 'false');
        btn.textContent = active ? '♥' : '♡';
      });
      this.renderList();
    },

    renderList() {
      const drawer = this.drawer;
      if (!drawer) return;
      const list = this.read();
      const listEl = $('[data-ch-wish-list]', drawer);
      const empty = $('[data-ch-wish-empty]', drawer);
      const foot = $('[data-ch-wish-foot]', drawer);
      const inCart = Cart.variantIds || new Set();

      empty.hidden = list.length > 0;
      foot.hidden = list.length === 0;

      listEl.innerHTML = list
        .map((item) => {
          const added = inCart.has(String(item.variant));
          const canMove = item.single && item.available;
          const image = item.image ? `<img src="${escapeHtml(item.image)}" alt="" loading="lazy">` : '';
          const was = item.compare > item.price ? `<span class="ch-wish__was">${money(item.compare)}</span>` : '';
          const action = canMove
            ? `<button type="button" class="ch-wish__move${added ? ' is-in-cart' : ''}" data-ch-wish-move="${escapeHtml(item.handle)}"${added ? ' disabled' : ''}>${added ? 'In cart ✓' : 'Move to cart'}</button>`
            : `<a href="${escapeHtml(item.url)}" class="ch-wish__move">${item.available ? 'Choose options' : 'Sold out'}</a>`;
          return `
            <div class="ch-line">
              <a href="${escapeHtml(item.url)}" class="ch-line__tile" aria-label="View product">${image}</a>
              <div class="ch-line__body">
                <div class="ch-line__top">
                  <a href="${escapeHtml(item.url)}" class="ch-line__name">${escapeHtml(item.title)}</a>
                  <button type="button" class="ch-wish__remove" aria-label="Remove from wishlist" data-ch-wish-remove="${escapeHtml(item.handle)}">×</button>
                </div>
                <div class="ch-wish__price"><span class="ch-wish__now">${money(item.price)}</span>${was}</div>
                ${action}
              </div>
            </div>`;
        })
        .join('');

      const total = list.reduce((sum, item) => sum + Number(item.price || 0), 0);
      const totalEl = $('[data-ch-wish-total]', drawer);
      if (totalEl) totalEl.textContent = money(total);
    },
  };

  /* ---------- Search ---------- */

  const Search = {
    cache: new Map(),

    phoneMatches(term) {
      const q = term.trim().toLowerCase();
      if (!q || !C.phone || !Array.isArray(C.phone.models)) return [];
      const out = [];
      C.phone.models.forEach(({ brand, models }) => {
        if (brand.toLowerCase().includes(q)) out.push({ brand, model: '' });
        (models || []).forEach((model) => {
          if (`${brand} ${model}`.toLowerCase().includes(q)) out.push({ brand, model });
        });
      });
      return out.slice(0, 4);
    },

    async fetchResults(term) {
      if (this.cache.has(term)) return this.cache.get(term);
      const url = `${R.search_url}?q=${encodeURIComponent(term)}&type=product&options%5Bprefix%5D=last&section_id=ch-search-results`;
      const html = await fetch(url).then((r) => r.text());
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const render = $('[data-ch-search-render]', doc);
      const markup = render ? render.innerHTML : '';
      this.cache.set(term, markup);
      return markup;
    },

    async update(scope, term) {
      const trending = $('[data-ch-search-trending]', scope);
      const phones = $('[data-ch-search-phones]', scope);
      const chips = $('[data-ch-search-phone-chips]', scope);
      const results = $('[data-ch-search-results]', scope);
      const clear = $('[data-ch-search-clear]', scope);
      const q = term.trim();

      if (clear) clear.hidden = !term;
      if (trending) trending.hidden = !!q;

      const matches = this.phoneMatches(q);
      if (phones && chips) {
        phones.hidden = matches.length === 0;
        const base = R.all_products_collection_url || '/collections/all';
        chips.innerHTML = matches
          .map(({ brand, model }) => {
            const label = model ? `${brand} ${model}` : `All ${brand} cases`;
            const params = new URLSearchParams({ brand });
            if (model) params.set('model', model);
            return `<a class="ch-chip" href="${base}?${params.toString()}">${escapeHtml(label)}</a>`;
          })
          .join('');
      }

      if (!q) {
        if (results) results.innerHTML = '';
        return;
      }

      scope._term = q;
      try {
        const markup = await this.fetchResults(q);
        if (scope._term !== q || !results) return;
        results.innerHTML = markup;
        const none = $('[data-ch-search-none]', results);
        if (none && matches.length) none.hidden = true;
      } catch (e) {}
    },

    openPanel(root) {
      const panel = $('[data-ch-search-panel]', root);
      const scrim = $('.ch-search__scrim', root);
      const input = $('[data-ch-search-input]', root);
      if (input && !input.value.trim()) this.update(root, '');
      if (panel) panel.hidden = false;
      if (scrim) scrim.hidden = false;
      root.classList.add('is-open');
    },

    closePanel(root) {
      const panel = $('[data-ch-search-panel]', root);
      const scrim = $('.ch-search__scrim', root);
      if (panel) panel.hidden = true;
      if (scrim) scrim.hidden = true;
      root.classList.remove('is-open');
    },
  };

  /* ---------- Cross-sell buttons (stay “Added” while in cart) ---------- */

  const syncCrossButtons = () => {
    const ids = Cart.variantIds || new Set();
    $$('[data-ch-cross-add]').forEach((btn) => {
      if (!btn.dataset.addLabel) btn.dataset.addLabel = btn.textContent.trim();
      const inCart = ids.has(String(btn.getAttribute('data-ch-quick-add')));
      btn.classList.toggle('is-added', inCart);
      btn.textContent = inCart ? btn.dataset.addedLabel || 'Added' : btn.dataset.addLabel;
    });
  };

  document.addEventListener('ch:cart:updated', syncCrossButtons);

  /* ---------- Count-up ---------- */

  const countUp = (root = document) => {
    const els = $$('[data-ch-countup]', root).filter((el) => !el._chCounted);
    if (!els.length) return;
    const run = (el) => {
      el._chCounted = true;
      const target = parseFloat(el.getAttribute('data-ch-countup')) || 0;
      const suffix = el.getAttribute('data-suffix') || '';
      const start = performance.now();
      const tick = (now) => {
        const p = Math.min(1, (now - start) / 1200);
        const value = Math.round(target * (1 - Math.pow(1 - p, 3)));
        el.textContent = value.toLocaleString('en-IN') + suffix;
        if (p < 1) requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    };
    if (!('IntersectionObserver' in window)) {
      els.forEach(run);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            io.unobserve(entry.target);
            run(entry.target);
          }
        });
      },
      { threshold: 0.2 }
    );
    els.forEach((el) => io.observe(el));
  };

  /* ---------- Events ---------- */

  document.addEventListener('click', async (event) => {
    const target = event.target;

    // PC search field
    const pcSearchInput = target.closest('[data-ch-search] [data-ch-search-input]');
    if (pcSearchInput) {
      Search.openPanel(pcSearchInput.closest('[data-ch-search]'));
      return;
    }

    // Cart open / close
    const cartOpen = target.closest('[data-ch-cart-open]');
    if (cartOpen && Cart.drawer) {
      event.preventDefault();
      closeLayer($('[data-ch-menu]'), false);
      Cart.open(cartOpen);
      return;
    }
    if (target.closest('[data-ch-cart-close]')) {
      event.preventDefault();
      Cart.close();
      return;
    }

    // Cart line change
    const lineBtn = target.closest('[data-ch-line-change]');
    if (lineBtn) {
      event.preventDefault();
      const qty = parseInt(lineBtn.getAttribute('data-qty'), 10);
      const current = parseInt(lineBtn.getAttribute('data-current') || '-1', 10);
      if (qty === current) return;
      try {
        await Cart.change(lineBtn.getAttribute('data-ch-line-change'), qty);
      } catch (e) {
        toast(e.message);
      }
      return;
    }

    // Quick add
    const quick = target.closest('[data-ch-quick-add]');
    if (quick) {
      event.preventDefault();
      event.stopPropagation();
      if (quick.classList.contains('is-loading')) return;
      quick.classList.add('is-loading');
      // Icon buttons (the + on a product card) swap their glyph with CSS, not text
      const iconOnly = quick.hasAttribute('data-ch-icon');
      const originalLabel = quick.textContent;
      try {
        await Cart.add([{ id: Number(quick.getAttribute('data-ch-quick-add')), quantity: 1 }], {
          silent: quick.getAttribute('data-silent') === 'true',
          title: quick.getAttribute('data-title') || '',
        });
        if (quick.hasAttribute('data-ch-cross-add')) {
          syncCrossButtons();
        } else if (document.contains(quick)) {
          quick.classList.add('is-added');
          if (!iconOnly) quick.textContent = (C.strings && C.strings.addedShort) || 'Added ✓';
          setTimeout(() => {
            quick.classList.remove('is-added');
            if (!iconOnly) quick.textContent = originalLabel;
          }, 1400);
        }
      } catch (e) {
        toast(e.message);
      } finally {
        quick.classList.remove('is-loading');
      }
      return;
    }

    // Wishlist heart
    const heart = target.closest('[data-ch-wish]');
    if (heart) {
      event.preventDefault();
      event.stopPropagation();
      try {
        const product = JSON.parse(heart.getAttribute('data-product'));
        Wish.toggle(product);
      } catch (e) {}
      return;
    }

    // Wishlist drawer
    const wishOpen = target.closest('[data-ch-wish-open]');
    if (wishOpen && Wish.drawer) {
      event.preventDefault();
      closeLayer($('[data-ch-menu]'), false);
      Wish.open(wishOpen);
      return;
    }
    if (target.closest('[data-ch-wish-close]')) {
      event.preventDefault();
      Wish.close();
      return;
    }
    const wishRemove = target.closest('[data-ch-wish-remove]');
    if (wishRemove) {
      Wish.remove(wishRemove.getAttribute('data-ch-wish-remove'));
      return;
    }
    const wishMove = target.closest('[data-ch-wish-move]');
    if (wishMove) {
      const handle = wishMove.getAttribute('data-ch-wish-move');
      const item = Wish.read().find((i) => i.handle === handle);
      if (!item) return;
      try {
        await Cart.add([{ id: Number(item.variant), quantity: 1 }], { silent: true, title: item.title });
        Wish.remove(handle);
      } catch (e) {
        toast(e.message);
      }
      return;
    }
    if (target.closest('[data-ch-wish-add-all]')) {
      const items = Wish.read().filter((i) => i.single && i.available);
      if (!items.length) return;
      try {
        await Cart.add(
          items.map((i) => ({ id: Number(i.variant), quantity: 1 })),
          { silent: true, title: items.length === 1 ? items[0].title : `${items.length} items` }
        );
        const moved = new Set(items.map((i) => i.handle));
        Wish.write(Wish.read().filter((i) => !moved.has(i.handle)));
        Wish.close();
        Cart.open();
      } catch (e) {
        toast(e.message);
      }
      return;
    }

    // Phone menu
    const menuOpen = target.closest('[data-ch-menu-open]');
    if (menuOpen) {
      event.preventDefault();
      openLayer($('[data-ch-menu]'), menuOpen);
      return;
    }
    if (target.closest('[data-ch-menu-close]')) {
      closeLayer($('[data-ch-menu]'));
    }

    // Search overlay (phone + compact PC)
    const searchOpen = target.closest('[data-ch-search-open]');
    if (searchOpen) {
      event.preventDefault();
      openLayer($('[data-ch-msearch]'), searchOpen);
      return;
    }
    const searchClose = target.closest('[data-ch-search-close]');
    if (searchClose) {
      const pcRoot = searchClose.closest('[data-ch-search]');
      if (pcRoot) Search.closePanel(pcRoot);
      else closeLayer($('[data-ch-msearch]'));
      return;
    }
    const searchClear = target.closest('[data-ch-search-clear]');
    if (searchClear) {
      event.preventDefault();
      const scope = searchClear.closest('[data-ch-search], [data-ch-msearch]');
      const input = $('[data-ch-search-input]', scope);
      input.value = '';
      input.focus();
      Search.update(scope, '');
      return;
    }

    // Bundle tiers
    const tier = target.closest('[data-ch-tier]');
    if (tier) {
      const group = tier.closest('[data-ch-tiers]');
      $$('[data-ch-tier]', group).forEach((btn) => {
        const active = btn === tier;
        btn.classList.toggle('is-active', active);
        btn.setAttribute('aria-pressed', active ? 'true' : 'false');
      });
      const cta = $('[data-ch-tier-cta]', group);
      if (cta) cta.textContent = tier.getAttribute('data-price-label') || '';
      const ctaLink = $('[data-ch-tier-link]', group);
      const link = tier.getAttribute('data-link');
      if (ctaLink && link) ctaLink.setAttribute('href', link);
    }
  });

  document.addEventListener('focusin', (event) => {
    const input = event.target.closest && event.target.closest('[data-ch-search] [data-ch-search-input]');
    if (input) Search.openPanel(input.closest('[data-ch-search]'));
  });

  document.addEventListener(
    'input',
    debounce((event) => {
      const input = event.target.closest && event.target.closest('[data-ch-search-input]');
      if (!input) return;
      const scope = input.closest('[data-ch-search], [data-ch-msearch]');
      if (scope.matches('[data-ch-search]')) Search.openPanel(scope);
      Search.update(scope, input.value);
    }, 180)
  );

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    const pc = $('[data-ch-search].is-open');
    if (pc) {
      Search.closePanel(pc);
      const input = $('[data-ch-search-input]', pc);
      if (input) input.blur();
      return;
    }
    const top = openLayers[openLayers.length - 1];
    if (top) closeLayer(top);
  });

  window.addEventListener('storage', (event) => {
    if (event.key === Wish.key) Wish.sync();
  });

  /* ---------- Theme editor ---------- */

  document.addEventListener('shopify:section:load', (event) => {
    Wish.sync();
    countUp(event.target);
  });

  document.addEventListener('shopify:section:select', (event) => {
    if (event.target.querySelector('[data-ch-cart-drawer]')) Cart.open();
  });

  document.addEventListener('shopify:section:deselect', (event) => {
    if (event.target.querySelector('[data-ch-cart-drawer]')) Cart.close();
  });

  /* ---------- Init ---------- */

  const init = () => {
    Wish.sync();
    countUp();
    if (document.querySelector('[data-ch-cross-add]')) Cart.refreshCount();
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  window.Chloia = { Cart, Wish, Search, toast, money, openLayer, closeLayer, countUp };
})();
